import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Pin } from 'lucide-react';
import { Separator } from '@helios/ui';
import {
  expandNavGroupsWithHubTabs,
  findDomainForPath,
  isRailDestinationActive,
  railPathFor,
} from '@/navigation/registry';
import { searchNavigation } from '@/navigation/search';
import { useAuth } from '@/features/auth/authContext';
import { isDomainExpanded } from '@/navigation/navPreferences';
import { useScopedNavPreferences } from '@/navigation/useScopedNavPreferences';
import type { NavDomain } from '@/navigation/types';
import { NavFilter } from './NavFilter';
import { NavGroupSection } from './NavGroupSection';
import { NavItem } from './NavItem';

interface NavTreeProps {
  /** Already RBAC- and visibility-filtered by the caller. */
  groups: NavDomain[];
  /** Sidebar rail (icon-only) mode. */
  collapsed?: boolean;
  onNavigate?: () => void;
}

/**
 * The two-level navigation tree shared by the desktop sidebar and the mobile
 * drawer: pinned shortcuts, filter-as-you-type, then seven collapsible domains.
 *
 * Expansion and pins persist per user (localStorage, via `useNavPreferences`);
 * the domain owning the current route is opened whenever that domain changes,
 * so you can always see where you are without losing a section you deliberately
 * closed.
 */
export function NavTree({ groups: configuredGroups, collapsed = false, onNavigate }: NavTreeProps) {
  // Empty sections remain drop targets in the editor, but ordinary navigation
  // should not offer a disclosure with nothing behind it.
  const groups = useMemo(
    () => configuredGroups.filter((group) => group.items.length > 0),
    [configuredGroups],
  );
  const instanceId = useId().replace(/:/g, '');
  const location = useLocation();
  const navigate = useNavigate();
  const permissions = useAuth().permissions;
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');

  const { expandedDomains, pinnedIds, toggleDomain, setDomainExpanded, togglePin, scope } = useScopedNavPreferences();

  const activeDomainId = findDomainForPath(groups, location.pathname)?.id;
  const autoExpanded = useRef<string | null>(null);

  useEffect(() => {
    // Open the domain you are in — once per domain change, so a section the
    // user then closes on purpose stays closed until they navigate elsewhere.
    const activeScope = `${scope ?? 'none'}/${activeDomainId ?? ''}`;
    if (activeDomainId && autoExpanded.current !== activeScope) {
      autoExpanded.current = activeScope;
      setDomainExpanded(activeDomainId, true);
    }
  }, [activeDomainId, setDomainExpanded, scope]);

  const searchableGroups = useMemo(
    () => expandNavGroupsWithHubTabs(groups, permissions),
    [groups, permissions],
  );

  const permitted = useMemo(
    () =>
      new Map(
        searchableGroups.flatMap((group) => group.items.map((item) => [item.id, item])),
      ),
    [searchableGroups],
  );

  // A pin left behind by a role change simply stops rendering — pins never
  // widen what a user may reach.
  const pinned = useMemo(
    () => pinnedIds.map((id) => permitted.get(id)).filter((item) => item !== undefined),
    [pinnedIds, permitted],
  );

  const results = useMemo(
    () => searchNavigation(searchableGroups, query, permissions),
    [searchableGroups, permissions, query],
  );
  const filtering = query.trim().length > 0;
  const listId = `nav-${instanceId}-list`;

  /** Visible destination rows, in DOM order, for arrow-key traversal. */
  const rows = useCallback(
    () =>
      Array.from(
        containerRef.current?.querySelectorAll<HTMLElement>('[data-nav-item]') ?? [],
      ).filter((element) => element.closest('[hidden]') === null),
    [],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && filtering) {
      event.preventDefault();
      setQuery('');
      inputRef.current?.focus();
      return;
    }

    if (event.key === 'Enter' && event.target === inputRef.current && results.length > 0) {
      event.preventDefault();
      const first = results[0];
      if (first) {
        setQuery('');
        navigate(railPathFor(first.destination, permissions));
        onNavigate?.();
      }
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const items = rows();
    if (items.length === 0) return;
    event.preventDefault();

    const index = items.indexOf(document.activeElement as HTMLElement);
    if (event.key === 'ArrowDown') {
      items[index < 0 ? 0 : Math.min(index + 1, items.length - 1)]?.focus();
      return;
    }
    if (index <= 0) {
      inputRef.current?.focus();
      return;
    }
    items[index - 1]?.focus();
  };

  if (collapsed) {
    return (
      <div ref={containerRef} onKeyDown={handleKeyDown}>
        {pinned.length > 0 ? (
          <ul aria-label="Pinned" className="space-y-0.5">
            {pinned.map((item) => (
              <NavItem
                key={item.id}
                to={railPathFor(item, permissions)}
                label={item.label}
                icon={item.icon}
                collapsed
                end={!item.matchPrefix}
                forceActive={isRailDestinationActive(item.id, location.pathname) || undefined}
              />
            ))}
          </ul>
        ) : null}
        <ul className="space-y-0.5">
          {groups.map((group, index) => (
            <NavGroupSection
              key={group.id}
              group={group}
              collapsed
              showRailDivider={index > 0 || pinned.length > 0}
            />
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div ref={containerRef} onKeyDown={handleKeyDown} className="space-y-1">
      <NavFilter
        ref={inputRef}
        value={query}
        onChange={setQuery}
        onClear={() => {
          setQuery('');
          inputRef.current?.focus();
        }}
        controlsId={listId}
        inputId={`nav-${instanceId}-filter`}
      />

      {filtering ? (
        <div id={listId}>
          <p
            className="text-2xs text-muted-foreground/70 flex h-6 items-center gap-2 px-2 font-semibold uppercase tracking-widest"
            aria-live="polite"
          >
            {results.length === 1 ? '1 match' : `${results.length} matches`}
          </p>
          {results.length === 0 ? (
            <p className="text-muted-foreground px-2 py-2 text-sm">
              Nothing matches “{query.trim()}”.
            </p>
          ) : (
            <ul aria-label="Filtered destinations" className="space-y-px">
              {results.map((result) => (
                <NavItem
                  key={result.destination.id}
                  to={railPathFor(result.destination, permissions)}
                  label={result.destination.label}
                  icon={result.destination.icon}
                  meta={result.domainLabel}
                  onNavigate={() => {
                    setQuery('');
                    onNavigate?.();
                  }}
                  pinned={pinnedIds.includes(result.destination.id)}
                  onTogglePin={() => togglePin(result.destination.id)}
                  end={!result.destination.matchPrefix}
                />
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div id={listId} className="space-y-1">
          {pinned.length > 0 ? (
            <div>
              <p className="text-2xs text-primary/70 flex h-6 items-center gap-2 px-2 font-semibold uppercase tracking-widest">
                <Pin className="h-3 w-3" aria-hidden="true" />
                Pinned
              </p>
              <ul aria-label="Pinned destinations" className="space-y-px">
                {pinned.map((item) => (
                <NavItem
                  key={item.id}
                  to={railPathFor(item, permissions)}
                  label={item.label}
                  icon={item.icon}
                  indented
                  onNavigate={onNavigate}
                  pinned
                  onTogglePin={() => togglePin(item.id)}
                  end={!item.matchPrefix}
                  forceActive={isRailDestinationActive(item.id, location.pathname) || undefined}
                />
                ))}
              </ul>
              <Separator className="my-1" />
            </div>
          ) : null}

          <ul className="space-y-0.5">
            {groups.map((group) => (
              <NavGroupSection
                key={group.id}
                group={group}
                expanded={isDomainExpanded(expandedDomains, group.id)}
                onToggle={() => toggleDomain(group.id)}
                onNavigate={onNavigate}
                pinnedIds={pinnedIds}
                onTogglePin={togglePin}
                idPrefix={`nav-${instanceId}`}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
